import { useState, type ReactNode } from 'react';
import { AlertCircle, Loader2 } from 'lucide-react';
import styles from './ConfirmDialog.module.css';

/**
 * Small generic confirm-action dialog. Sibling of DisclaimerModal
 * but tuned for a yes/no decision rather than a single-action ack.
 *
 * First consumer: the ChargingPage Cancel handler when the charger
 * has already entered Charging state — the driver may not realise
 * the session started and tapping Cancel should ask before
 * dispatching RemoteStopTransaction.
 *
 * Behaviour:
 *   - Backdrop click + ESC are NOT escape hatches; user must pick
 *     one button explicitly. Matches DisclaimerModal — destructive
 *     decisions shouldn't slip through implicit dismissal.
 *   - confirmTone defaults to 'danger' (red CTA, fits the
 *     "stop charging" framing). Pass 'accent' for non-destructive
 *     confirm-throughs.
 *   - onConfirm may return a Promise; the button stays disabled
 *     while it resolves and rejections surface inline.
 *   - cancel button is required (this is a yes/no dialog, not an
 *     ack — both options must be available).
 */
export interface ConfirmDialogProps {
  title: string;
  body: ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  confirmTone?: 'danger' | 'accent';
  onConfirm: () => void | Promise<void>;
  onCancel: () => void;
}

export function ConfirmDialog({
  title, body, confirmLabel, cancelLabel = 'Cancel',
  confirmTone = 'danger', onConfirm, onCancel,
}: ConfirmDialogProps) {
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const handleConfirm = async (): Promise<void> => {
    setSubmitting(true);
    setErrorMsg('');
    try {
      await onConfirm();
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : 'Action failed');
      setSubmitting(false);
    }
    // On success: parent tears down the dialog via state change.
    // Leaving `submitting` true so the brief flicker is hidden by
    // unmount. Matches DisclaimerModal's pattern.
  };

  const confirmClass = confirmTone === 'danger'
    ? `${styles.confirmBtn} ${styles.confirmDanger}`
    : `${styles.confirmBtn} ${styles.confirmAccent}`;

  return (
    <div className={styles.overlay} role="dialog" aria-modal aria-labelledby="evi-confirm-title">
      <div className={styles.card}>
        <div className={styles.iconWrap} aria-hidden>
          <AlertCircle size={28} />
        </div>
        <h2 id="evi-confirm-title" className={styles.title}>{title}</h2>
        <div className={styles.body}>{body}</div>

        {errorMsg && (
          <div className={styles.error} role="alert">{errorMsg}</div>
        )}

        <div className={styles.actions}>
          <button
            type="button"
            className={styles.cancelBtn}
            onClick={onCancel}
            disabled={submitting}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            className={confirmClass}
            onClick={handleConfirm}
            disabled={submitting}
          >
            {submitting
              ? <><Loader2 size={14} className={styles.spin} aria-hidden /> Working…</>
              : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
